import { Injectable } from '@angular/core';
import { IsActiveMatchOptions } from '@angular/router';
import { DateTime } from 'luxon';
import { DateFilter } from '@zopup/zop-shared';

@Injectable({ providedIn: 'root' })
export class ZopUtilsService {
  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = true".
   */
  get exactMatchOptions(): IsActiveMatchOptions {
    return {
      paths: 'exact',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'exact',
    };
  }

  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = false".
   */
  get subsetMatchOptions(): IsActiveMatchOptions {
    return {
      paths: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'subset',
    };
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Generates a random id
   *
   * @param length
   */
  randomId(length: number = 10): string {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let name = '';

    for (let i = 0; i < 10; i++) {
      name += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return name;
  }

  /**
   * Period helper function that calculates date ranges based on filter type
   *
   * @param dateFilterType The type of date filter to apply
   * @param fromTime Optional custom from date (used with CUSTOM filter)
   * @param toTime Optional custom to date (used with CUSTOM filter)
   * @returns Object containing calculated fromTime and toTime
   */
  periodHelper(
    dateFilterType: string,
    fromTime?: DateTime,
    toTime?: DateTime
  ): { fromTime: DateTime; toTime: DateTime } {
    try {
      let resultFromTime: DateTime;
      let resultToTime: DateTime;

      switch (dateFilterType) {
        case DateFilter.TODAY:
          resultFromTime = DateTime.now().startOf('day');
          resultToTime = DateTime.now().endOf('day');
          break;
        case DateFilter.YESTERDAY:
          resultFromTime = DateTime.now().minus({ days: 1 }).startOf('day');
          resultToTime = DateTime.now().minus({ days: 1 }).endOf('day');
          break;
        case DateFilter.CURRENT_WEEK:
          resultFromTime = DateTime.now().startOf('week').startOf('day');
          resultToTime = DateTime.now().endOf('day');
          break;
        case DateFilter.LAST_WEEK:
          resultFromTime = DateTime.now().startOf('week').minus({ days: 7 }).startOf('day');
          resultToTime = DateTime.now().startOf('week').minus({ days: 1 }).endOf('day');
          break;
        case DateFilter.CURRENT_MONTH:
          resultFromTime = DateTime.now().startOf('month');
          resultToTime = DateTime.now().endOf('day');
          break;
        case DateFilter.LAST_MONHTH:
          resultFromTime = DateTime.now().minus({ months: 1 }).startOf('month');
          resultToTime = DateTime.now().minus({ months: 1 }).endOf('month');
          break;
        case DateFilter.CURRENT_QUARTER:
          resultFromTime = DateTime.now().startOf('quarter').startOf('day');
          resultToTime = DateTime.now().endOf('day');
          break;
        case DateFilter.LAST_QUARTER:
          resultFromTime = DateTime.now().minus({ quarters: 1 }).startOf('quarter').startOf('day');
          resultToTime = DateTime.now().minus({ quarters: 1 }).endOf('quarter').endOf('day');
          break;
        case DateFilter.LAST_FINANCIAL_YEAR:
          resultFromTime = DateTime.now().month >= 4
            ? DateTime.local(DateTime.now().year - 1, 4, 1).startOf('day')
            : DateTime.local(DateTime.now().year - 2, 4, 1).startOf('day');
          resultToTime = DateTime.now().month >= 4
            ? DateTime.local(DateTime.now().year, 3, 31).endOf('day')
            : DateTime.local(DateTime.now().year - 1, 3, 31).endOf('day');
          break;
        case DateFilter.CURRENT_FINANCIAL_YEAR:
          resultFromTime = DateTime.now().month >= 4
            ? DateTime.local(DateTime.now().year, 4, 1).startOf('day')
            : DateTime.local(DateTime.now().year - 1, 4, 1).startOf('day');
          resultToTime = DateTime.now().endOf('day');
          break;
        case DateFilter.CUSTOM:
          // For custom, use provided dates but ensure they are start/end of day
          resultFromTime = fromTime ? fromTime.startOf('day') : DateTime.now().startOf('day');
          resultToTime = toTime ? toTime.endOf('day') : DateTime.now().endOf('day');
          break;
        default:
          // Default to current week if unknown filter type
          resultFromTime = DateTime.now().startOf('week').startOf('day');
          resultToTime = DateTime.now().endOf('day');
          break;
      }

      return { fromTime: resultFromTime, toTime: resultToTime };
    } catch (error) {
      console.error('Exception in ZopUtilsService => periodHelper()', error);
      // Return current week as fallback
      return {
        fromTime: DateTime.now().startOf('week').startOf('day'),
        toTime: DateTime.now().endOf('day')
      };
    }
  }
}
